import { useState, useMemo } from "react";
import CalculatorShell from "@/components/CalculatorShell";
import Field from "@/components/Field";
import ResultStat from "@/components/ResultStat";
import { toNum, rentVsBuy } from "@/lib/finance";
import { fmtUSD } from "@/lib/format";

export default function RentVsBuyPage() {
    const [homePrice, setHomePrice] = useState(450000);
    const [downPaymentPct, setDown] = useState(20);
    const [mortgageRate, setMortgageRate] = useState(6.5);
    const [mortgageYears, setMortgageYears] = useState(30);
    const [propertyTaxRate, setPropertyTax] = useState(1.1);
    const [maintenanceRate, setMaint] = useState(1);
    const [homeAppreciation, setApprec] = useState(3);
    const [monthlyRent, setRent] = useState(2400);
    const [rentIncrease, setRentInc] = useState(3);
    const [investReturn, setInvest] = useState(7);
    const [years, setYears] = useState(7);

    const r = useMemo(
        () =>
            rentVsBuy({
                homePrice: toNum(homePrice),
                downPaymentPct: toNum(downPaymentPct),
                mortgageRate: toNum(mortgageRate),
                mortgageYears: toNum(mortgageYears),
                propertyTaxRate: toNum(propertyTaxRate),
                maintenanceRate: toNum(maintenanceRate),
                homeAppreciation: toNum(homeAppreciation),
                monthlyRent: toNum(monthlyRent),
                rentIncrease: toNum(rentIncrease),
                investReturn: toNum(investReturn),
                years: Math.max(1, toNum(years)),
            }),
        [homePrice, downPaymentPct, mortgageRate, mortgageYears, propertyTaxRate, maintenanceRate, homeAppreciation, monthlyRent, rentIncrease, investReturn, years],
    );

    const diff = Math.abs(r.buyNet - r.rentNet);

    return (
        <CalculatorShell
            slug="rent-vs-buy"
            explainer={
                             <>
                    <h2>How this rent vs. buy calculator works</h2>
                    <p>
                        A rent vs. buy calculator compares the total financial cost of renting against buying
                        over a chosen time horizon. It accounts for opportunity cost, appreciation, equity
                        buildup, tax benefits, and all major costs on both sides — giving you a complete
                        picture rather than just comparing rent to a mortgage payment.
                    </p>
                    <h3>How it works</h3>
                    <ul>
                        <li><strong>Buying costs:</strong> include mortgage payment, property taxes, insurance, HOA fees, maintenance (typically 1–2% of value annually), and upfront closing costs.</li>
                        <li><strong>Renting costs:</strong> monthly rent plus renters insurance, adjusted for annual rent increases over the time horizon.</li>
                        <li><strong>Equity accumulation:</strong> combines down payment, principal paydown, and home price appreciation.</li>
                        <li><strong>Opportunity cost:</strong> the down payment invested in a diversified portfolio would also grow — this is subtracted from the buying advantage.</li>
                        <li><strong>Break-even year:</strong> the point at which total buying costs fall below total renting costs over the same period.</li>
                    </ul>
                    <h3>Tips</h3>
                    <ul>
                        <li>If you plan to move within three to four years, renting is almost always the financially superior choice.</li>
                        <li>Model multiple appreciation scenarios — 2%, 3.5%, and 5% — since appreciation assumptions drive the result more than any other variable.</li>
                        <li>Don't ignore the 1% annual maintenance estimate; a $500,000 home realistically costs $5,000/year in upkeep on average.</li>
                    </ul>
                </>
            }
        >
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                <div className="lg:col-span-3 bg-card rounded-xl border border-border p-6 sm:p-8 space-y-5">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-primary/70 mb-1 font-medium">Buying</div>
                    <Field label="Home price" prefix="$" value={homePrice} onChange={setHomePrice} dataTestId="calc-input-price" />
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Down payment" suffix="%" value={downPaymentPct} onChange={setDown} dataTestId="calc-input-down" />
                        <Field label="Mortgage rate" suffix="%" value={mortgageRate} onChange={setMortgageRate} step={0.125} dataTestId="calc-input-rate" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Property tax" suffix="%" value={propertyTaxRate} onChange={setPropertyTax} step={0.1} dataTestId="calc-input-ptax" />
                        <Field label="Maintenance" suffix="%" value={maintenanceRate} onChange={setMaint} step={0.1} dataTestId="calc-input-maint" />
                    </div>
                    <Field label="Home appreciation" suffix="%" value={homeAppreciation} onChange={setApprec} step={0.25} dataTestId="calc-input-apprec" />

                    <div className="pt-3 border-t border-border text-[11px] uppercase tracking-[0.2em] text-primary/70 font-medium">Renting</div>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Monthly rent" prefix="$" value={monthlyRent} onChange={setRent} dataTestId="calc-input-rent" />
                        <Field label="Rent increase" suffix="%" value={rentIncrease} onChange={setRentInc} step={0.25} dataTestId="calc-input-rentinc" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Investment return" suffix="%" value={investReturn} onChange={setInvest} step={0.25} dataTestId="calc-input-invest" />
                        <Field label="Time horizon" suffix="yrs" value={years} onChange={setYears} dataTestId="calc-input-years" />
                    </div>
                </div>
                <div className="lg:col-span-2 space-y-4">
                    <div className={`rounded-2xl p-6 ${r.winner === "buy" ? "bg-primary text-primary-foreground" : "bg-card border border-border text-primary"}`}>
                        <div className={`text-[10px] uppercase tracking-[0.2em] ${r.winner === "buy" ? "text-accent-bright" : "text-muted-foreground"}`}>
                            Winner over {toNum(years)} years
                        </div>
                        <div className="font-mono tabular text-5xl mt-3 capitalize" data-testid="calc-result-winner">
                            {r.winner}
                        </div>
                        <div className={`mt-2 text-sm ${r.winner === "buy" ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                            By {fmtUSD(diff, { decimals: 0 })} in net cost
                        </div>
                    </div>
                    <div className="bg-card border border-border rounded-2xl p-6 space-y-3">
                        <div className="flex items-baseline justify-between">
                            <span className="text-xs text-muted-foreground">Buy net cost</span>
                            <span className="font-mono tabular text-lg" data-testid="calc-result-buy">{fmtUSD(r.buyNet, { decimals: 0 })}</span>
                        </div>
                        <div className="flex items-baseline justify-between">
                            <span className="text-xs text-muted-foreground">Rent net cost</span>
                            <span className="font-mono tabular text-lg" data-testid="calc-result-rent">{fmtUSD(r.rentNet, { decimals: 0 })}</span>
                        </div>
                        <div className="pt-3 border-t border-border space-y-2 text-xs">
                            <div className="flex justify-between"><span className="text-muted-foreground">Home equity after {toNum(years)}y</span><span className="font-mono tabular">{fmtUSD(r.homeValue - r.loanBalance, { decimals: 0 })}</span></div>
                            <div className="flex justify-between"><span className="text-muted-foreground">Renter&apos;s portfolio</span><span className="font-mono tabular">{fmtUSD(r.investPortfolio, { decimals: 0 })}</span></div>
                        </div>
                    </div>
                </div>
            </div>
        </CalculatorShell>
    );
}
